import MaterialTable from 'material-table';
import React, { useEffect, useState } from 'react';

import { ICampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_models';
import { getDebugCampaignPacingData } from '~/src/dash/api/pacing/controller';
import { IDebugCampaignPacingModel } from '~/src/dash/api/pacing/models';

export interface IProps {
  campaignID: string;
  publisherID: string;
  totalGoal: string;
  prebidInfo?: ICampaignPrebidInfo;
}

interface ITableData {
  site_id: string;
  weight: number;
  requestsAfterRenderRateEMAPacing: string;
  renderRateLastHour: string;
  dice_rollpercentage_ema_pacing: string;
  has_spiky_traffic_pattern: boolean;
  peak_requests_from_last_hour: string;
  num_significant_spikes_last_hour: string;
  num_active_min_included_in_ema_calc: string;
}

function numberWithCommas(x: number, maxDigits: number = 2): string {
  return x ? x.toFixed(maxDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0';
}

export const CampaignDebugPacingTable = (props: IProps) => {
  const [pacingData, setPacingData] = useState(null as any as IDebugCampaignPacingModel);
  const [processedData, setProcessedData] = useState(null as any as ITableData[]);
  const [msg, setMsg] = useState('');

  function processCampaignData(data: IDebugCampaignPacingModel) {
    const out = [] as ITableData[];
    if (data.formula_data_by_site_id === null || data.formula_data_by_site_id === undefined) {
      setMsg(
        'No data found, you must have at least 1 campaign with Low Pacing (< 150 req/h) OR Channel Weights enabled + at least 1 request on the last 6 hours',
      );
      return;
    }

    for (const [key, value] of Object.entries(data.formula_data_by_site_id)) {
      out.push({
        site_id: key,
        weight: value.weight,
        requestsAfterRenderRateEMAPacing: numberWithCommas(
          value.debug_pacing_ema_requests_data?.RenderedSiteReqEMAScaledToHourUsingAvgRenderRate
            ? value.debug_pacing_ema_requests_data?.RenderedSiteReqEMAScaledToHourUsingAvgRenderRate
            : 0,
        ),
        renderRateLastHour:
          numberWithCommas(
            (value.debug_pacing_ema_requests_data?.RenderRateForLastHour
              ? value.debug_pacing_ema_requests_data.RenderRateForLastHour
              : 0) * 100,
          ) + '%',
        dice_rollpercentage_ema_pacing: value.ema_dice_rollpercentage?.toFixed(4) + '%',
        has_spiky_traffic_pattern: value.debug_pacing_ema_requests_data?.HasSpikeyLiveTrafficPattern
          ? value.debug_pacing_ema_requests_data?.HasSpikeyLiveTrafficPattern
          : false,
        peak_requests_from_last_hour: numberWithCommas(
          value.debug_pacing_ema_requests_data?.PeakRequestsFromLastHour
            ? value.debug_pacing_ema_requests_data?.PeakRequestsFromLastHour
            : 0,
        ),
        num_significant_spikes_last_hour: numberWithCommas(
          value.debug_pacing_ema_requests_data?.NumSignificantSpikesDuringLastHour
            ? value.debug_pacing_ema_requests_data?.NumSignificantSpikesDuringLastHour
            : 0,
        ),
        num_active_min_included_in_ema_calc: numberWithCommas(
          value.debug_pacing_ema_requests_data?.NumActiveMinIncludedInEMACalc
            ? value.debug_pacing_ema_requests_data?.NumActiveMinIncludedInEMACalc
            : 0,
        ),
      });
    }

    setProcessedData(out);
  }

  const fetchCampaignPacingData = () => {
    getDebugCampaignPacingData(props.publisherID, props.campaignID).then(([data, err]) => {
      if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
        setPacingData(data);
        setMsg('');
        try {
          processCampaignData(data);
        } catch (e) {
          console.error(e);
          setMsg(
            'Error while processing campaign data. Please contact your publica account manager',
          );
        }
      }
      if (err !== undefined) {
        setMsg(
          "Can't fetch pacing data. Your campaign may not be low pacing or data missing in prebid infrasync cache for the current time period.",
        );
        console.error(err);
      }
    });
  };

  useEffect(() => {
    fetchCampaignPacingData();
  }, [props.campaignID, props.publisherID]);

  if (msg !== '') {
    return <p>{msg}</p>;
  }

  if (pacingData == null || processedData == null) {
    return <p>Loading...</p>;
  }

  const renderTable = (): React.ReactElement => {
    if ((processedData || []).length === 0) {
      return (
        <>
          <p>No data found, you must have at least:</p>
          <ul>
            <li>1 site with EMA Pacing Data, calculated from Request data from the last 60 mins</li>
          </ul>
        </>
      );
    }
    return (
      <>
        <MaterialTable
          title=''
          columns={[
            { title: 'Channel ID', field: 'site_id' },
            { title: 'Weight', field: 'weight', type: 'numeric', searchable: false },
            {
              title: 'Pacing Rate (EMA)',
              field: 'dice_rollpercentage_ema_pacing',
              searchable: false,
            },
            {
              title: 'Last 60min Requests EMA after render rate',
              field: 'requestsAfterRenderRateEMAPacing',
              searchable: false,
            },
            {
              title: 'Last 60min Peak Requests For A Single Min',
              field: 'peak_requests_from_last_hour',
              searchable: false,
            },
            {
              title: 'Last 60min Render Rate (EMA)',
              field: 'renderRateLastHour',
              searchable: false,
            },
            {
              title: 'Last 60min Had Spikey Traffic (EMA)',
              field: 'has_spiky_traffic_pattern',
              searchable: false,
            },
            {
              title: 'Last 60min Number Significant Spikes (EMA)',
              field: 'num_significant_spikes_last_hour',
              searchable: false,
            },
            {
              title: 'Last 60min Total Min Included in EMA Calc',
              field: 'num_active_min_included_in_ema_calc',
              searchable: false,
            },
          ]}
          options={{
            search: true,
            pageSize: 15,
            pageSizeOptions: [15, 30, 100],
          }}
          data={processedData}
        />
      </>
    );
  };

  return (
    <div>
      <p id={`pb-pacing-data-total-goal`}>
        Total Goal: {numberWithCommas(Number(props.totalGoal))}
      </p>
      <p id={`pb-pacing-data-cumulative-goal`}>
        Cumulative Pacing Goal: {numberWithCommas(pacingData.cumulative_pacing_goal)}
      </p>
      <p id={`pb-pacing-data-total-imps`}>
        Total Impressions:{' '}
        {numberWithCommas(
          pacingData.total_impressions_realtime > pacingData.total_impressions
            ? pacingData.total_impressions_realtime
            : pacingData.total_impressions,
        )}
      </p>
      <p id={`pb-pacing-data-hour-goal`}>
        Goal for this hour: {numberWithCommas(pacingData.goal_for_this_hour)}
      </p>
      <p id={`pb-pacing-data-sum-weights`}>
        Sum of Weights: {numberWithCommas(pacingData.sum_of_weights)}
      </p>
      <p id={`pb-pacing-data-hour-number`}>
        Number of hours: {numberWithCommas(pacingData.NumberOfHours)}
      </p>
      <p id={`pb-pacing-data-hour-number-afterdayparting`}>
        Number of hours (after dayparting):{' '}
        {numberWithCommas(pacingData.NumberOfHourAfterDayparting)}
      </p>
      <p id={`pb-pacing-data-current-hour-number`}>
        Current number of hours (after dayparting):{' '}
        {numberWithCommas(pacingData.NumberOfHoursDoneAfterDayparting)}
      </p>
      <p id={`pb-pacing-data-low-pacing`}>
        Low Pacing: {(props.prebidInfo || { low_pacing: false }).low_pacing ? 'true' : 'false'}
      </p>
      {renderTable()}
      <h3>Update of "end date" field:</h3>
      <p id={`pb-pacing-data-hour-last-update-time`}>
        Time of last update: {pacingData.PacingUpdateTime}
      </p>
      <p id={`pb-pacing-data-hour-pacing-update-nbr-imps`}>
        Nbr of impression(s) at update: {numberWithCommas(pacingData.PacingUpdateNbrImps)}
      </p>
    </div>
  );
};
